/**
 * Angle 3D configurator accessibility helpers.
 *
 * - Label quantity minus/plus buttons and the number field
 * - Add alt on the Angle loading logo when missing
 * - Name the 3D model viewer / poster (model-viewer `alt` + poster img)
 * Re-runs when the configurator mutates the DOM.
 */
(function () {
  const WRAPPER = '.tda-customizer-wrapper';
  const QTY_GROUP = '.qty-input-group';
  const LOGO = 'img[src*="cdn.fivelive.co/angle3d/"]';
  const VIEWER = 'tda-model-viewer';

  function productPreviewName() {
    const title = (document.title || '')
      .replace(/\s*\|\s*Stone Forest\s*$/i, '')
      .trim();
    return title ? '3D preview of ' + title : '3D product preview';
  }

  /**
   * @param {ParentNode} root
   */
  function labelQuantityControls(root) {
    root.querySelectorAll(QTY_GROUP).forEach((group) => {
      const minus = group.querySelector('.button-minus');
      const plus = group.querySelector('.button-plus');
      const qty = group.querySelector('.quantity-field');
      if (!qty) return;

      if (!qty.id) {
        qty.id = 'tda-quantity-' + Math.random().toString(36).slice(2, 9);
      }

      qty.setAttribute('aria-label', 'Quantity');

      if (!group.querySelector('label[for="' + CSS.escape(qty.id) + '"]')) {
        const label = document.createElement('label');
        label.className = 'visually-hidden';
        label.htmlFor = qty.id;
        label.textContent = 'Quantity';
        group.insertBefore(label, group.firstChild);
      }

      if (minus) minus.setAttribute('aria-label', 'Decrease quantity');
      if (plus) plus.setAttribute('aria-label', 'Increase quantity');
    });
  }

  /**
   * @param {ParentNode} root
   */
  function labelLoadingLogo(root) {
    root.querySelectorAll(LOGO).forEach((img) => {
      if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
      img.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * @param {ParentNode} root
   */
  function labelModelViewer(root) {
    const name = productPreviewName();

    root.querySelectorAll(VIEWER).forEach((viewer) => {
      if (!viewer.getAttribute('alt')) viewer.setAttribute('alt', name);
      if (!viewer.getAttribute('aria-label')) viewer.setAttribute('aria-label', name);

      if (!viewer.querySelector('[slot="poster"]')) {
        const posterSrc = viewer.getAttribute('poster');
        if (posterSrc) {
          const img = document.createElement('img');
          img.slot = 'poster';
          img.src = posterSrc;
          img.alt = name;
          viewer.appendChild(img);
        }
      }

      const sr = viewer.shadowRoot;
      if (!sr) return;

      const userInput = sr.querySelector('.userInput[role="img"]');
      if (userInput && !userInput.getAttribute('aria-label')) {
        userInput.setAttribute('aria-label', name);
      }

      const posterBtn = sr.querySelector('#default-poster');
      if (posterBtn && !posterBtn.getAttribute('aria-label')) {
        posterBtn.setAttribute('aria-label', name);
      }

      sr.querySelectorAll('img').forEach((img) => {
        if (img.hasAttribute('alt')) return;
        if (
          img.classList.contains('pdf-export-icon') ||
          /pdf-export/i.test(img.id || '')
        ) {
          img.setAttribute('alt', 'Download PDF');
          return;
        }
        img.setAttribute('alt', '');
      });
    });
  }

  /**
   * @param {ParentNode} [root]
   */
  function enhance(root) {
    const scope = root || document;
    labelQuantityControls(scope);
    labelLoadingLogo(scope);
    labelModelViewer(scope);
  }

  function init() {
    enhance();

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        enhance();
      });
    });

    const observe = (target) => {
      observer.observe(target, { childList: true, subtree: true });
    };

    const wrapper = document.querySelector(WRAPPER);
    if (wrapper) {
      observe(wrapper);
      return;
    }

    observe(document.documentElement);
    const boot = new MutationObserver(() => {
      const el = document.querySelector(WRAPPER);
      if (!el) return;
      boot.disconnect();
      observer.disconnect();
      observe(el);
      enhance(el);
    });
    boot.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
